// ─────────────────────────────────────────────────────────────────
// progressionEngine.js — Progression Domain
//
// Pure functions only. No side effects. No React. No Supabase.
// Testable in isolation.
//
// Domain ownership:
//   progressionEngine.js → mastery scoring, error classification,
//                          state machine, stability calculation
//   useLearningEngine.js → calls these functions, owns side effects
// ─────────────────────────────────────────────────────────────────

import { MASTERY_WEIGHTS, MASTERY_THRESHOLDS } from '../constants/appConstants.js';

// ─────────────────────────────────────────────
// MASTERY SCORING
// M = wA·A + wS·S + wT·T + wR·R
// Weights shift by learning stage — early sessions weight accuracy,
// later sessions weight transfer and stability.
// ─────────────────────────────────────────────
export function calcMasteryScore(accuracy, stability, transfer, recall, level = 'early') {
  const w = MASTERY_WEIGHTS[level] || MASTERY_WEIGHTS.early;
  return (w.wA * accuracy) + (w.wS * stability) + (w.wT * transfer) + (w.wR * recall);
}

export function checkMasteryGate(accuracy, stability, transfer, masteryScore) {
  const t = MASTERY_THRESHOLDS.advance;
  return masteryScore >= t.M && accuracy >= t.A && stability >= t.S && transfer >= t.T;
}

// ─────────────────────────────────────────────
// STABILITY CALCULATOR
// 1 - variance across recent attempts.
// Stable learners get correct answers consistently, not by luck.
// ─────────────────────────────────────────────
export function calcStability(accuracyHistory = []) {
  if (accuracyHistory.length < 2) return 0;
  const mean     = accuracyHistory.reduce((a, b) => a + b, 0) / accuracyHistory.length;
  const variance = accuracyHistory.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / accuracyHistory.length;
  return Math.max(0, 1 - Math.sqrt(variance));
}

// ─────────────────────────────────────────────
// MASTERY STATE LABEL
// ─────────────────────────────────────────────
export function getMasteryState(accuracy) {
  if (accuracy >= 0.90) return 'mastered';
  if (accuracy >= 0.65) return 'understood';
  return 'struggling';
}

// ─────────────────────────────────────────────
// ERROR CLASSIFIER
// Deterministic routing — maps error signals to remediation strategy.
// ─────────────────────────────────────────────
export function classifyError(signals = {}) {
  const { confidentAndWrong, missingVocab, consistentlyWrong, nearMiss, overloaded } = signals;
  if (confidentAndWrong && consistentlyWrong) return 'REFRAME';
  if (missingVocab || signals.prerequisiteGap) return 'REMEDIATE';
  if (overloaded || signals.scatteredErrors)   return 'SIMPLIFY';
  if (nearMiss || signals.unstable)            return 'REPEAT';
  return 'SIMPLIFY';
}

// ─────────────────────────────────────────────
// STATE MACHINE
// All session phase transitions route through getNextState.
// No ad-hoc setSessionPhase calls anywhere in the system.
// ─────────────────────────────────────────────
export const STATE_TRANSITIONS = {
  PROFILE:     { PROFILE_COMPLETE:      'LESSON'      },
  LESSON:      { CHECKPOINT_PRESENTED:  'CHECKPOINT'  },
  CHECKPOINT:  { ANSWER_CORRECT:        'VALIDATION',
                 ANSWER_INCORRECT:      'REMEDIATION' },
  REMEDIATION: { RETRY_READY:           'CHECKPOINT'  },
  VALIDATION:  { MASTERY_CONFIRMED:     'ADVANCEMENT',
                 MASTERY_INSUFFICIENT:  'LESSON'      },
  ADVANCEMENT: { LESSON_ADVANCED:       'LESSON'      },
};

export function getNextState(currentPhase, event) {
  return STATE_TRANSITIONS[currentPhase]?.[event] || currentPhase;
}
